import * as THREE from 'three';

import { ELEMENT_COLOR } from './palette.ts';
import { AssetRegistry } from './assets/registry.ts';
import { projectileRole } from './assets/roles.ts';
import {
  accentFor, lookFor, tierGlow, tierScale, type ProjectileShape, type SpellLook,
} from './spellLook.ts';
import { ALL_SPELLS } from '../core/spells.ts';
import type { Element } from '../core/constants.ts';
import type { Projectile, SimEvent, SimState } from '../core/sim.ts';

/**
 * Everything a spell looks like between leaving a hand and landing.
 *
 * Two rules shape the whole file. The first is readability: shapes stay simple
 * and silhouettes stay distinct, because a room with twenty spells crossing it
 * is only fun if you can still tell which one is yours. The second is that this
 * has to run on ordinary hardware, which rules out a particle system per spell —
 * so lights are pooled, impacts are pooled, geometry is shared, and a trail is
 * one stretched cone rather than a ribbon rebuilt every frame.
 *
 * Nothing here is authoritative. It draws what core reports and nothing else,
 * so a dropped frame costs a visual and never a hit.
 */

/** Concurrent dynamic lights the spells are allowed between them. */
const LIGHT_BUDGET = 8;
/** Concurrent impacts. Older ones are recycled rather than queued. */
const IMPACT_POOL = 14;
const IMPACT_SECONDS = 0.32;
/**
 * A bolt's strike, which is shorter than a spell's impact.
 *
 * An explosion lingers because there is debris and smoke in the fiction of it. A
 * bolt hitting a wall is a spark — it is over before you have finished looking
 * at it, and a strike that hung around as long as a Fire Explosion would make
 * every shot read as a blast.
 */
const STRIKE_SECONDS = 0.16;
/** Conjured debris does not linger; it goes out like the spell that made it. */
const DEBRIS_SECONDS = 1.4;
const DEBRIS_GRAVITY = 14;
/** How long a spell takes to slide from the wand tip onto its real path. */
const MUZZLE_BLEND = 0.1;
/** Where the warm-up parks its objects: under the arena, out of every shot. */
const BELOW = { x: 0, y: -400, z: 0 };

interface Live {
  group: THREE.Group;
  body: THREE.Mesh;
  trail: THREE.Mesh | null;
  look: SpellLook;
  /** Radians turned so far, kept here so spin survives a variable frame. */
  angle: number;
  /**
   * How far the wand tip was from where the simulation actually spawned this,
   * decaying to nothing over MUZZLE_BLEND.
   *
   * The simulation fires from the eye and must keep doing so — a spell that
   * genuinely left the wand would leave at an angle to the crosshair and land
   * beside whatever was aimed at. So the visual starts at the tip and slides
   * onto the true path inside a tenth of a second, which nobody sees and which
   * makes the spell look like it came out of the wand it came out of.
   */
  muzzleOffset: THREE.Vector3 | null;
  age: number;
}

interface Impact {
  shell: THREE.Mesh;
  life: number;
  style: string;
  scale: number;
  /** How long it had to begin with, so the fade knows how far through it is. */
  span: number;
}

interface Debris {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  life: number;
}

/** One geometry per shape, shared by every instance of it. */
function shapeGeometry(shape: ProjectileShape): THREE.BufferGeometry {
  switch (shape) {
    case 'sprite':
      // A unit card. Turned to face the camera every frame, so its orientation
      // here does not matter and its size is set by the projectile's scale.
      return new THREE.PlaneGeometry(1, 1);
    case 'shard':
      return new THREE.OctahedronGeometry(1, 0);
    case 'mass':
      return new THREE.DodecahedronGeometry(1, 0);
    case 'stone':
      return new THREE.DodecahedronGeometry(1, 0);
    case 'blade':
      // Flat and wide, so the spin reads as a blade turning over rather than a
      // lump rolling. Thin enough to nearly vanish edge-on, which is the tell.
      return new THREE.BoxGeometry(2.2, 0.16, 0.7);
    case 'mace':
      return new THREE.SphereGeometry(1, 8, 6);
    case 'barb':
      return new THREE.TetrahedronGeometry(1.3, 0);
    case 'orb':
    default:
      return new THREE.SphereGeometry(1, 12, 10);
  }
}

/**
 * What makes two spells draw the same way.
 *
 * Exported so the warm-up and the test that guards it read from one definition.
 * Every field here is one that changes a *material*, and a material the warm-up
 * did not build is a shader compiled the first time somebody casts — which is a
 * hitch in the middle of a fight, arriving through the very parameters added to
 * make the spells tunable. Adding a knob to `SpellLook` that affects a material
 * means adding it here too.
 */
export function warmKey(look: SpellLook, element: Element): string {
  return [
    look.shape, element, look.trail > 0, look.debris,
    look.halo > 0, accentFor(look, element),
    // Every opacity is part of a material key, so every one of them is part of
    // this. A value the warm-up never saw is a shader compiled mid-fight.
    look.trailOpacity, look.haloOpacity, look.bodyOpacity,
  ].join('|');
}

/**
 * How strongly the trail draws where it meets the projectile, as a fraction of
 * how strongly it draws at the tail.
 *
 * Not zero: a streak that vanishes entirely at the head detaches from the thing
 * making it and reads as a puff of smoke travelling in company.
 */
const TRAIL_HEAD_FADE = 0.12;

/**
 * The trail cone, with the fade baked into its vertices.
 *
 * A trail is at its most useful behind the projectile and at its most harmful on
 * top of it. At the head it sits over the very thing it is meant to be drawing
 * attention to — which for a modelled spell is the model — and additive blending
 * means it does not merely cover it but adds to it, so the axe and the streak sum
 * to something brighter and flatter than either. Everywhere else it is doing its
 * job. So it starts at nearly nothing where it meets the projectile and reaches
 * full strength at the tail.
 *
 * Done as vertex colours rather than a texture or a shader. Vertex colour
 * multiplies the material's colour, and multiplying an *additive* contribution
 * by zero is the same as being absent — so a gradient from black at the apex to
 * white at the base is a fade from nothing to full, with no second material, no
 * alpha sorting, and no custom shader to keep in step with the rest of the file.
 *
 * Baked into the shared geometry, so it costs one buffer for the whole game
 * rather than one per spell. The consequence is that the fade is global: it is a
 * property of what a trail *is*, not a per-spell knob. If a spell wants a
 * heavier streak it has `trailOpacity` and `trailWidth`, both of which scale
 * this rather than fighting it.
 */
function fadedCone(): THREE.BufferGeometry {
  const geo = new THREE.ConeGeometry(1, 1, 8, 1, true);
  const pos = geo.getAttribute('position');
  const colours = new Float32Array(pos.count * 3);

  for (let i = 0; i < pos.count; i++) {
    /*
     * The cone's apex is at +0.5 and its base at −0.5, and the mesh is turned so
     * the apex rides with the projectile. So height maps directly to how far
     * back along the streak a vertex is: 0 at the head, 1 at the tail.
     */
    const back = 0.5 - pos.getY(i);
    const shade = TRAIL_HEAD_FADE + (1 - TRAIL_HEAD_FADE) * back;
    colours[i * 3] = shade;
    colours[i * 3 + 1] = shade;
    colours[i * 3 + 2] = shade;
  }

  geo.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  return geo;
}

export class SpellVfx {
  private readonly live = new Map<number, Live>();
  private readonly impacts: Impact[] = [];
  private readonly debris: Debris[] = [];
  /**
   * One of every spell visual, built during loading and never disposed.
   *
   * Held because Three.js frees a compiled program when the last material using
   * it is disposed — so a warm-up that tidies up after itself has done nothing.
   */
  private readonly warm: THREE.Object3D[] = [];

  /** Shared per shape, disposed once at teardown rather than per projectile. */
  private readonly geometries = new Map<ProjectileShape, THREE.BufferGeometry>();
  /**
   * One material per look, shared the same way.
   *
   * A busy fight spawns projectiles several times a second and every one was
   * allocating three or four fresh materials. Three.js caches the *program* by
   * signature so nothing recompiled, but the objects were garbage — and a
   * collection pause mid-fight is indistinguishable from the stutter all of this
   * exists to remove.
   *
   * Safe to share because nothing writes to a projectile's material after it is
   * made: colour comes from the element, and the element does not change in
   * flight.
   */
  private readonly materials = new Map<string, THREE.MeshBasicMaterial>();
  private readonly trailGeo = fadedCone();
  private readonly art = new Map<string, THREE.Texture>();
  private readonly shellGeo = new THREE.SphereGeometry(1, 14, 10);
  private readonly debrisGeo = new THREE.TetrahedronGeometry(1, 0);

  /**
   * Lights are rented, not owned.
   *
   * A point light per projectile is the obvious thing and the wrong one: every
   * one of them costs every lit material in the scene, and a busy fight would
   * blow through the shader's light limit and take the frame rate with it. Eight
   * follow the nearest spells instead, which is indistinguishable in play.
   */
  private readonly lights: THREE.PointLight[] = [];

  private readonly scene: THREE.Scene;
  private readonly assets: AssetRegistry;

  constructor(scene: THREE.Scene, assets: AssetRegistry = AssetRegistry.grayBox()) {
    this.scene = scene;
    this.assets = assets;
    /*
     * Always visible, never toggled. This is the freeze on the first cast.
     *
     * Three.js builds a material's program against the number of *visible*
     * lights in the scene, and `numPointLights` is part of the program cache
     * key. Eight lights created hidden meant everything compiled during loading
     * against the torches alone — and the first spell to make one visible
     * changed the count, which invalidated the program of every lit material in
     * the arena at once. Every wall, floor, pillar, prop and mage recompiled in
     * the middle of a fight, and again at two spells, and again at three.
     *
     * Intensity is what gets modulated instead. A light at zero contributes
     * nothing and costs one unused term in a loop the shader was already
     * running; changing it does not touch the program. So the count is fixed
     * from the moment the scene is built, which is what the loading screen needs
     * in order to be able to warm anything at all.
     */
    for (let i = 0; i < LIGHT_BUDGET; i++) {
      const light = new THREE.PointLight(0xffffff, 0, 12, 2);
      scene.add(light);
      this.lights.push(light);
    }
  }

  /** A shared material, made once per distinct look. */
  private materialFor(
    key: string, make: () => THREE.MeshBasicMaterial,
  ): THREE.MeshBasicMaterial {
    let material = this.materials.get(key);
    if (!material) {
      material = make();
      this.materials.set(key, material);
    }
    return material;
  }

  /**
   * The drawing behind a sprite spell, loaded once and shared.
   *
   * Kept in a map rather than on the material, because two spells could
   * reasonably share one drawing at different opacities and a texture is not
   * cheap enough to load twice for that.
   */
  private artFor(name: string): THREE.Texture {
    let art = this.art.get(name);
    if (!art) {
      /*
       * `TextureLoader` reaches for `Image`, which does not exist in Node — and
       * the warm-up runs in tests, where the point is the material signatures
       * rather than the pixels. A blank texture has the same signature.
       */
      art = typeof document === 'undefined'
        ? new THREE.Texture()
        : new THREE.TextureLoader().load(`/sprites/${name}.png`);
      art.colorSpace = THREE.SRGBColorSpace;
      this.art.set(name, art);
    }
    return art;
  }

  private geometryFor(shape: ProjectileShape): THREE.BufferGeometry {
    let geo = this.geometries.get(shape);
    if (!geo) {
      geo = shapeGeometry(shape);
      this.geometries.set(shape, geo);
    }
    return geo;
  }

  // ── Projectiles ────────────────────────────────────────────────────────────

  /** Mirrors core's projectile list, and moves what is already there. */
  syncProjectiles(
    state: SimState,
    camera: THREE.Camera,
    dt: number,
    localId?: string,
    muzzle?: THREE.Vector3,
  ): void {
    const seen = new Set<number>();

    for (const pr of state.projectiles) {
      seen.add(pr.id);
      let entry = this.live.get(pr.id);
      if (!entry) {
        entry = this.spawn(pr);
        // Only the local mage's own spells. Everyone else's wand is somewhere
        // the camera cannot see, and the correction would be to a point behind
        // their back.
        if (muzzle && pr.ownerId === localId) {
          entry.muzzleOffset = muzzle.clone().sub(new THREE.Vector3(pr.pos.x, pr.pos.y, pr.pos.z));
        }
        this.live.set(pr.id, entry);
      }

      entry.age += dt;
      let ox = 0;
      let oy = 0;
      let oz = 0;
      if (entry.muzzleOffset) {
        const left = 1 - entry.age / MUZZLE_BLEND;
        if (left <= 0) entry.muzzleOffset = null;
        else {
          // Squared, so it leaves the tip decisively and arrives on the true
          // path without a visible corner.
          const k = left * left;
          ox = entry.muzzleOffset.x * k;
          oy = entry.muzzleOffset.y * k;
          oz = entry.muzzleOffset.z * k;
        }
      }

      entry.group.position.set(pr.pos.x + ox, pr.pos.y + oy, pr.pos.z + oz);

      // Face the way it is going, then spin about that axis. Doing it in this
      // order is what makes a blade turn like a thrown blade rather than wobble.
      const heading = new THREE.Vector3(pr.dir.x, pr.dir.y, pr.dir.z);
      entry.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), heading);

      /*
       * A drawing turns to face the camera, not the way it is travelling.
       *
       * The group is already turned along the heading, so the card is turned
       * back out of that and onto the view — which is what makes a flat picture
       * read as a ball of lightning rather than as a flat picture. Done on the
       * body rather than the group so the trail and halo keep their heading.
       */
      if (entry.look.shape === 'sprite') {
        entry.body.quaternion.copy(camera.quaternion);
        entry.body.quaternion.premultiply(entry.group.quaternion.clone().invert());
        entry.angle += entry.look.spin * dt * Math.PI * 2;
        if (entry.look.spin !== 0) entry.body.rotateZ(entry.angle);
        continue;
      }

      entry.angle += entry.look.spin * dt * Math.PI * 2;
      const a = entry.angle;
      switch (entry.look.spinAxis) {
        case 'x': entry.body.rotation.set(a, 0, 0); break;
        case 'y': entry.body.rotation.set(0, a, 0); break;
        case 'z': entry.body.rotation.set(0, 0, a); break;
        // No right way up to preserve, so it wobbles.
        default: entry.body.rotation.set(a, a * 0.35, 0); break;
      }
    }

    for (const [id, entry] of this.live) {
      if (seen.has(id)) continue;
      this.retire(entry);
      this.live.delete(id);
    }

    this.assignLights(state, camera);
  }

  private spawn(pr: Projectile): Live {
    const look = lookFor(pr.spellId);
    const colour = ELEMENT_COLOR[pr.element];
    /*
     * What the light around this spell is coloured, which is not always what the
     * spell is made of. Element still decides for everything but the handful of
     * spells whose identity and whose element disagree — see `accentFor`.
     */
    const accent = accentFor(look, pr.element);
    const size = look.size * tierScale(pr.tier);

    const group = new THREE.Group();

    // Additive and unlit: a spell is a light source, not a lit object, and
    // additive blending is what makes two of them overlapping read as brighter
    // rather than as one occluding the other.
    /*
     * A model if the manifest names one for this shape, and the procedural
     * geometry otherwise.
     *
     * The two are interchangeable because the grammar was always per shape: a
     * spell throws a `mace`, and whether that is an authored axe or a faceted
     * lump is a question about the manifest rather than about the spell. So a
     * model arrives by being named, and nothing else changes.
     */
    const modelled = this.assets.usesModel(projectileRole(look.shape));
    if (modelled) {
      const model = this.assets.create({
        role: projectileRole(look.shape),
        // An object keeps its own colours; energy takes the element's.
        color: look.tinted ? colour : undefined,
      });
      // Tier only. A model arrives at its true size from the manifest, where a
      // procedural shape is a unit that has to be scaled to `look.size` — and
      // multiplying by both put a quarter-metre axe on screen at six
      // centimetres, which is small enough to look like nothing at all.
      model.scale.setScalar(tierScale(pr.tier));
      group.add(model);
      /*
       * Behind the model, so the shell reads as light coming off it rather than
       * as a bubble around it. Added first for the same reason: additive
       * material with `depthWrite` off, so draw order is what decides which
       * looks like it is in front.
       */
      if (look.halo > 0) group.add(this.haloFor(look, size, pr.tier, accent));
      if (look.trail > 0) group.add(this.trailFor(pr, look, size, accent));
      this.scene.add(group);
      return {
        group, body: model as THREE.Mesh, trail: null, look,
        angle: 0, muzzleOffset: null, age: 0,
      };
    }

    /*
     * Void keeps its own opacity: it is drawn with normal blending as a dark
     * core rather than as a light, so the additive stacking `bodyOpacity` exists
     * to control does not apply to it, and turning it down would only make the
     * absence semi-transparent.
     */
    const opacity = pr.element === 'void' ? 0.95 : look.bodyOpacity;
    const blending = pr.element === 'void' ? THREE.NormalBlending : THREE.AdditiveBlending;

    /*
     * A drawing on a card, for the spells that are drawn rather than built.
     *
     * Returned early because almost nothing below applies: a sprite has no
     * white-hot core added to it and no rim, since whoever drew it already put
     * those in. Geometry parameters exist to make a sphere look like a spell; a
     * drawing does not need talking into it.
     */
    if (look.shape === 'sprite' && look.art) {
      const card = new THREE.Mesh(
        this.geometryFor('sprite'),
        this.materialFor(`art|${look.art}|${opacity}`, () => new THREE.MeshBasicMaterial({
          map: this.artFor(look.art!),
          transparent: true,
          opacity,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          // The drawing carries its own colour; tinting it would fight the
          // artist for control of a thing they have already decided.
          color: 0xffffff,
        })),
      );
      card.scale.setScalar(size);
      group.add(card);
      if (look.halo > 0) group.add(this.haloFor(look, size, pr.tier, accent));
      if (look.trail > 0) group.add(this.trailFor(pr, look, size, accent));
      // Added here, because this branch returns before the shared tail of the
      // method that would otherwise have done it — which is exactly how a
      // perfectly built sprite ended up in no scene at all.
      this.scene.add(group);
      return { group, body: card, trail: null, look, angle: 0, muzzleOffset: null, age: 0 };
    }

    const body = new THREE.Mesh(
      this.geometryFor(look.shape),
      this.materialFor(`body|${colour}|${opacity}|${blending}`, () => new THREE.MeshBasicMaterial({
        color: colour,
        transparent: true,
        opacity,
        blending,
        depthWrite: false,
      })),
    );
    body.scale.setScalar(size);

    // Void is the exception to everything: it reads as an absence, so it is a
    // dark core with a bright rim rather than a glow. Against the fog that is
    // far more legible than another coloured light would be.
    if (pr.element === 'void') {
      const rim = new THREE.Mesh(
        this.geometryFor(look.shape),
        this.materialFor('rim', () => new THREE.MeshBasicMaterial({
          color: colour, transparent: true, opacity: 0.55,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide,
        })),
      );
      rim.scale.setScalar(1.45);
      body.add(rim);
    } else {
      // A white-hot core inside the coloured body. This is most of what makes an
      // energy spell read as hot rather than as a coloured ball.
      const core = new THREE.Mesh(
        this.geometryFor('orb'),
        this.materialFor('core', () => new THREE.MeshBasicMaterial({
          color: 0xffffff, transparent: true, opacity: 0.85,
          blending: THREE.AdditiveBlending, depthWrite: false,
        })),
      );
      core.scale.setScalar(0.55);
      body.add(core);
    }

    group.add(body);
    if (look.halo > 0) group.add(this.haloFor(look, size, pr.tier, accent));

    let trail: THREE.Mesh | null = null;
    if (look.trail > 0) {
      trail = this.trailFor(pr, look, size, accent);
      group.add(trail);
    }

    this.scene.add(group);
    return { group, body, trail, look, angle: 0, muzzleOffset: null, age: 0 };
  }

  /**
   * Takes a spent projectile off the scene.
   *
   * Nothing is disposed: geometry and materials are shared with every other
   * projectile of the same look, and disposing one would take its compiled
   * program with it — putting a compile back into the middle of the next fight,
   * which is the whole thing this exists to avoid.
   */
  /**
   * The streak behind a projectile.
   *
   * One stretched cone pointing back down the path. A ribbon of past positions
   * looks marginally better and costs a geometry rebuild per projectile per
   * frame, which is the whole budget for no real gain.
   *
   * Built the same either way: a modelled axe still trails the element it was
   * cast with, which is what keeps a Cleric's axe reading as a Cleric's.
   */
  private trailFor(
    pr: Projectile, look: SpellLook, size: number, colour: number,
  ): THREE.Mesh {
    const trail = new THREE.Mesh(
      this.trailGeo,
      this.materialFor(
        `trail|${colour}|${look.trailOpacity}`,
        () => new THREE.MeshBasicMaterial({
          color: colour, transparent: true, opacity: look.trailOpacity,
          blending: THREE.AdditiveBlending, depthWrite: false,
          /*
           * Front faces only, and this is why changing `trailOpacity` used to do
           * so little. The cone is open-ended, so `DoubleSide` drew the far wall
           * and the near wall of the same tube — and because the blending is
           * additive, that is not one surface seen through another but the same
           * contribution applied twice. Every trail in the game was rendering at
           * double the opacity it asked for, and halving a number that was then
           * doubled is how a change gets lost.
           */
          side: THREE.FrontSide,
          // The head-to-tail fade lives in the geometry. See `fadedCone`.
          vertexColors: true,
        }),
      ),
    );
    const length = look.trail * tierScale(pr.tier);
    const width = size * look.trailWidth;
    trail.scale.set(width, length, width);
    trail.rotation.x = Math.PI / 2;
    trail.position.z = -length / 2;
    return trail;
  }

  /**
   * A soft shell of light around the projectile itself.
   *
   * Not the same thing as the pooled point light, and worth keeping the two
   * apart: `glow` is what a spell does to the *room*, and this is what the spell
   * looks like. A point light illuminates everything except the object it is
   * attached to, so it does nothing whatever for legibility.
   *
   * Which is fine for an additive orb, because an orb *is* light. It is not fine
   * for an authored model: a bronze axe is a lit object like any other, so in a
   * dark cathedral it is a dark shape against a dark wall. This is what gives it
   * an outline to find.
   */
  private haloFor(look: SpellLook, size: number, tier: number, colour: number): THREE.Mesh {
    const halo = new THREE.Mesh(
      this.geometryFor('orb'),
      this.materialFor(`halo|${colour}|${look.haloOpacity}`, () => new THREE.MeshBasicMaterial({
        color: colour, transparent: true, opacity: look.haloOpacity,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })),
    );
    halo.scale.setScalar(size * look.halo * tierScale(tier));
    return halo;
  }

  private retire(entry: Live): void {
    this.scene.remove(entry.group);
  }

  /** Hands the pooled lights to whichever spells are closest to the eye. */
  private assignLights(state: SimState, camera: THREE.Camera): void {
    const eye = camera.position;
    const ranked = state.projectiles
      .map((pr) => ({
        pr,
        d: (pr.pos.x - eye.x) ** 2 + (pr.pos.y - eye.y) ** 2 + (pr.pos.z - eye.z) ** 2,
      }))
      .sort((a, b) => a.d - b.d)
      .slice(0, LIGHT_BUDGET);

    this.lights.forEach((light, i) => {
      const pick = ranked[i];
      if (!pick) {
        // Dimmed, not hidden — see the constructor. Hiding it would change the
        // scene's light count and recompile every lit material in the arena.
        light.intensity = 0;
        return;
      }
      const look = lookFor(pick.pr.spellId);
      light.color.setHex(accentFor(look, pick.pr.element));
      light.intensity = look.glow * tierGlow(pick.pr.tier);
      light.distance = 6 + look.glow;
      light.position.set(pick.pr.pos.x, pick.pr.pos.y, pick.pr.pos.z);
    });
  }

  // ── Impacts ────────────────────────────────────────────────────────────────

  /** Reads core's impact events. Everything else here follows from them. */
  ingest(events: readonly SimEvent[]): void {
    for (const ev of events) {
      if (ev.type !== 'impact') continue;
      const look = lookFor(ev.spellId);
      const colour = ELEMENT_COLOR[ev.element];
      const accent = accentFor(look, ev.element);

      // A blast draws to its real radius, so what looked dangerous was.
      const reach = ev.radius > 0 ? ev.radius : look.size * 6 * tierScale(ev.tier);
      this.burst(ev.pos, accent, reach, look.impact);

      /*
       * Debris keeps the element's colour, not the accent.
       *
       * The flash is magic and takes the accent with it; the fragments are
       * pieces of a bronze axe lying on the floor. Tinting those violet would be
       * the same mistake as tinting the axe — and the spell already says which
       * it is, because `tinted: false` is exactly the claim that this thing is an
       * object rather than a light.
       */
      if (look.debris && ev.against !== 'expired') {
        this.scatter(ev.pos, ev.dir, colour, look, ev.tier);
      }
    }
  }

  /**
   * A small, quick flash where a hitscan bolt landed.
   *
   * The public face of `burst`, at bolt scale. Kept separate from the impact
   * path because that one is driven by core's `impact` events and sizes itself
   * from a spell's blast radius — a bolt has no radius, and borrowing one would
   * draw a Fire Explosion every time somebody fired a Fire Bolt.
   */
  strike(at: { x: number; y: number; z: number }, colour: number, reach: number): void {
    this.burst(at, colour, reach, 'flash', STRIKE_SECONDS);
  }

  private burst(
    at: { x: number; y: number; z: number }, colour: number, reach: number, style: string,
    life = IMPACT_SECONDS,
  ): void {
    // Recycled oldest-first rather than queued: a fight that outruns the pool
    // should drop the effect that has already been seen, not the newest one.
    if (this.impacts.length >= IMPACT_POOL) {
      // Removed, not disposed. Its material is a clone of one whose program is
      // shared, and disposing the last clone would take that program with it —
      // which is the fault this whole system exists to avoid, arriving through
      // the pool that was supposed to bound the cost.
      const oldest = this.impacts.shift();
      if (oldest) this.scene.remove(oldest.shell);
    }

    // Cloned from a shared original rather than built fresh: the shell fades as
    // it expands, so each one needs its own opacity — but cloning a material
    // whose program is already built costs nothing to compile.
    const shell = new THREE.Mesh(
      this.shellGeo,
      this.materialFor(`shell|${style}|${colour}`, () => new THREE.MeshBasicMaterial({
        color: style === 'implode' ? 0x1b1030 : colour,
        transparent: true,
        opacity: 0.7,
        blending: style === 'implode' ? THREE.NormalBlending : THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      })).clone(),
    );
    shell.position.set(at.x, at.y, at.z);
    // Void collapses inward, so it starts wide and closes. Everything else opens.
    shell.scale.setScalar(style === 'implode' ? reach : reach * 0.15);

    this.scene.add(shell);
    this.impacts.push({ shell, life, style, scale: reach, span: life });
  }

  private scatter(
    at: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    colour: number,
    look: SpellLook,
    tier: number,
  ): void {
    // Three pieces, not thirty. The floor is where signs live, and signs are
    // deliberately small and dim — litter that reads as a trap is the one
    // misread that costs somebody a life.
    for (let i = 0; i < 3; i++) {
      const mesh = new THREE.Mesh(
        this.debrisGeo,
        this.materialFor(`debris|${colour}`, () => new THREE.MeshBasicMaterial({
          color: colour, transparent: true, opacity: 0.8, depthWrite: false,
        })).clone(),
      );
      mesh.scale.setScalar(look.size * tierScale(tier) * 0.45);
      mesh.position.set(at.x, at.y, at.z);
      this.scene.add(mesh);

      this.debris.push({
        mesh,
        // Mostly back the way it came, with enough spread that three pieces do
        // not travel as one.
        vel: new THREE.Vector3(
          -dir.x * 2 + (Math.random() - 0.5) * 3,
          1.5 + Math.random() * 2.5,
          -dir.z * 2 + (Math.random() - 0.5) * 3,
        ),
        spin: new THREE.Vector3(
          (Math.random() - 0.5) * 14,
          (Math.random() - 0.5) * 14,
          (Math.random() - 0.5) * 14,
        ),
        life: DEBRIS_SECONDS,
      });
    }
  }

  // ── Per-frame ──────────────────────────────────────────────────────────────

  tick(dt: number): void {
    for (let i = this.impacts.length - 1; i >= 0; i--) {
      const im = this.impacts[i];
      im.life -= dt;
      const t = Math.max(0, im.life / (im.span ?? IMPACT_SECONDS));

      const mat = im.shell.material as THREE.MeshBasicMaterial;
      if (im.style === 'implode') {
        // Inward and darkening, the opposite of every other spell in the game.
        im.shell.scale.setScalar(im.scale * t);
        mat.opacity = 0.75 * (1 - t);
      } else {
        im.shell.scale.setScalar(im.scale * (0.15 + (1 - t) * 0.95));
        mat.opacity = 0.7 * t;
      }

      if (im.life > 0) continue;
      this.scene.remove(im.shell);
      this.impacts.splice(i, 1);
    }

    for (let i = this.debris.length - 1; i >= 0; i--) {
      const d = this.debris[i];
      d.life -= dt;
      d.vel.y -= DEBRIS_GRAVITY * dt;
      d.mesh.position.addScaledVector(d.vel, dt);
      d.mesh.rotation.x += d.spin.x * dt;
      d.mesh.rotation.y += d.spin.y * dt;
      d.mesh.rotation.z += d.spin.z * dt;

      // Conjured, so it goes out rather than settling. The fade is over the last
      // half of its life, which reads as the magic leaving it.
      const t = Math.max(0, d.life / DEBRIS_SECONDS);
      (d.mesh.material as THREE.MeshBasicMaterial).opacity = 0.8 * Math.min(1, t * 2);

      if (d.life > 0) continue;
      this.scene.remove(d.mesh);
      this.debris.splice(i, 1);
    }
  }

  /**
   * Puts one of everything into the scene so its shaders get built.
   *
   * Nothing a spell draws exists until somebody casts, so a compile pass over
   * the scene finds none of it and the first Fire Ball of a match pays for its
   * program in the middle of a fight.
   *
   * The subtlety, and the reason an earlier version of this did nothing at all:
   * Three.js reference-counts compiled programs against the materials that use
   * them, and disposing a material releases its program. Building one of
   * everything and then throwing it away threw away exactly the work it had just
   * done. So these are **kept**, parked far below the arena and hidden after the
   * warm frames have been drawn. A few dozen materials held in memory is a
   * cheaper thing to carry than a compile in the middle of a fight.
   */
  warmUp(park: { x: number; y: number; z: number } = BELOW): void {
    if (this.warm.length) return;

    const seen = new Set<string>();
    for (const spell of ALL_SPELLS) {
      const look = lookFor(spell.id);
      const key = warmKey(look, spell.element);
      if (seen.has(key)) continue;
      seen.add(key);

      const entry = this.spawn({
        id: -1, ownerId: '', faction: 'order', spellId: spell.id,
        element: spell.element, dice: { count: 1, sides: 1 },
        pos: park, dir: { x: 0, y: 0, z: 1 },
        speed: 0, travelled: 0, maxRange: 1, radius: 0, tier: 1,
      } as Projectile);
      entry.group.position.set(park.x, park.y, park.z);
      this.warm.push(entry.group);

      // Debris is a different material again — opaque, depth-written, and only
      // ever made at the moment something lands.
      if (look.debris) {
        this.scatter(park, { x: 0, y: 0, z: 1 }, ELEMENT_COLOR[spell.element], look, 1);
      }
    }

    /*
     * Every impact style, in every colour one can actually arrive in.
     *
     * White alone was not enough, and that is the hitch on the first spell to
     * hit a wall. A shell's material is keyed by style *and* colour, so warming
     * five white ones warmed five materials nothing would ever use again — the
     * first real impact built its own, and paid for it in the middle of a fight.
     *
     * Thirty-five materials sounds like a lot and is not: colour is not part of
     * a shader's signature, so they share compiled programs between them and the
     * cost is a first draw each, which is exactly what a warm-up is for.
     */
    /*
     * Built from the spells themselves rather than from the palette.
     *
     * The colour a burst is drawn in is the *accent* — which for most spells is
     * the element's colour and for some is not, Wounding's violet being the
     * obvious one. Warming the element palette alone therefore missed exactly
     * the spells that were interesting enough to be given their own colour, and
     * paid for them on the first hit.
     *
     * Taking the pairs from `ALL_SPELLS` means this cannot drift: a spell with a
     * new accent or a new impact style is warmed because it exists, not because
     * somebody remembered to add it here.
     */
    const impacts = new Set<string>();
    for (const spell of ALL_SPELLS) {
      const look = lookFor(spell.id);
      const accent = accentFor(look, spell.element);
      impacts.add(`${look.impact}|${accent}`);
    }
    for (const pair of impacts) {
      const [style, colour] = pair.split('|');
      this.burst(park, Number(colour), 1, style!);
    }

    /*
     * And the debris, which is keyed by colour alone and was warmed only for
     * the handful of spells that carry it.
     */
    for (const colour of new Set(Object.values(ELEMENT_COLOR))) {
      this.scatter(park, { x: 0, y: 0, z: 1 }, colour, lookFor('stone_fist'), 1);
    }

    // Everything made above is now in `live`, `impacts` and `debris`. Move it
    // all into the warm list so the per-frame tick stops touching it and, more
    // to the point, so nothing ever disposes it.
    for (const entry of this.live.values()) this.warm.push(entry.group);
    this.live.clear();
    for (const im of this.impacts) this.warm.push(im.shell);
    this.impacts.length = 0;
    for (const d of this.debris) this.warm.push(d.mesh);
    this.debris.length = 0;

    // Parked under the arena, so culling would drop them before they were ever
    // submitted and nothing would be compiled at all.
    for (const object of this.warm) {
      object.traverse((n) => { n.frustumCulled = false; });
    }
  }

  /**
   * Hides the warm-up objects once the frames that compiled them are drawn.
   *
   * Hidden, not disposed. `visible = false` costs nothing to render and keeps
   * the program alive; `dispose()` would give back the program and put the hitch
   * straight back into the first fight.
   */
  coolDown(): void {
    for (const object of this.warm) object.visible = false;
  }

  dispose(): void {
    for (const entry of this.live.values()) this.retire(entry);
    this.live.clear();
    for (const geo of this.geometries.values()) geo.dispose();
    for (const mat of this.materials.values()) mat.dispose();
    this.materials.clear();
    this.trailGeo.dispose();
    this.shellGeo.dispose();
    this.debrisGeo.dispose();
  }
}

export type { Element };
